import { CheckCircle } from '@project-lary/react-material-symbols-400-rounded';
import { useMarkChannelReadMutation } from 'gcs-common/clients/api/entities/channels/channel-api';
import { useGetCurrentUser } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { MESSAGE_TYPE } from 'gcs-common/constants/MessageTypes';
import { Button, ButtonVariant } from 'gcs-common/design-system/ui/button';
import { CHANNEL_TYPE } from 'gcs-common/helper/channelTypeHelper';
import FEATURE_FLAGS from 'gcs-common/helper/featureFlagsHelper';
import { UserType } from 'gcs-common/slices/administration/administrationUsers/administrationUsersConstants';
import {
  getChannel,
  getChannelType,
  getMemberCraftsmenCountForChannel,
} from 'gcs-common/slices/channel/channelSelectors';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { getHasFeatureFlag } from 'gcs-common/slices/currentUser/currentUserSelectors';
import {
  getChannelInputStagedForwardMessageIds,
  getChannelInputStagedReplyMessageId,
} from 'gcs-common/slices/messageInput/messageInputSelector';
import { getMessagesForChannelExcludingCraftsmanNotifications } from 'gcs-common/slices/messages/messagesSelectors';
import type { components } from 'gcs-common/types/openapi';
import { useSelector } from 'react-redux';

type Props = {
  showMarkLabel?: boolean;
  showDisabledState?: boolean;
};

function MarkReadButton({ showMarkLabel = true, showDisabledState = false }: Props) {
  const markMutation = useMarkChannelReadMutation();
  const { data: currentUser } = useGetCurrentUser();

  const selectedChannelId = useSelector(getSelectedChannelId) as string | undefined;
  const channel = useSelector(getChannel(selectedChannelId)) as
    | components['schemas']['ChatChannelCreatedDTO']
    | undefined;
  const channelMessages = useSelector(getMessagesForChannelExcludingCraftsmanNotifications(selectedChannelId)) as
    | components['schemas']['ChatMessageCreatedDTO'][]
    | undefined;
  const hasFeatureFlag = useSelector(getHasFeatureFlag(FEATURE_FLAGS.MARK_READ_ENABLED));
  const channelType = useSelector(getChannelType(selectedChannelId));
  const craftsmenCountForChannel = useSelector(getMemberCraftsmenCountForChannel(selectedChannelId));
  const isDirectChatWithoutCraftsman = channelType === CHANNEL_TYPE.DIRECT_CHAT && craftsmenCountForChannel === 0;
  const isForwarding = useSelector(getChannelInputStagedForwardMessageIds(selectedChannelId))?.length > 0;
  const isReplying = !!useSelector(getChannelInputStagedReplyMessageId(selectedChannelId));

  async function handleClick() {
    await markMutation.mutateAsync({ channelId: selectedChannelId! });
  }

  if (
    !hasFeatureFlag ||
    currentUser?.userType === UserType.CRAFTSMAN ||
    channelType === CHANNEL_TYPE.INTERNAL_AGENT ||
    channelType === CHANNEL_TYPE.INTERNAL_CRAFTSMAN ||
    isDirectChatWithoutCraftsman ||
    !selectedChannelId ||
    !channel ||
    !channelMessages?.length ||
    isForwarding ||
    isReplying
  ) {
    return null;
  }

  const lastMessage = channelMessages[channelMessages.length - 1];
  const isMarked = lastMessage?.messageType === MESSAGE_TYPE.MARK_READ;

  if (isMarked && !showDisabledState) {
    return null;
  }

  return (
    <Button
      variant={ButtonVariant.soft_accent}
      onClick={handleClick}
      isLoading={markMutation.isPending}
      disabled={isMarked || markMutation.isPending}
    >
      <div className="flex items-center gap-2">
        <CheckCircle />
        {showMarkLabel ? 'Erledigt' : null}
      </div>
    </Button>
  );
}

export default MarkReadButton;
